import { createFileRoute, redirect, notFound, Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import {
  findCity,
  type Country,
} from "@/lib/gold-data";
import { cityHeadMeta } from "@/lib/seo";
import { normalizeTypoPath } from "@/lib/typo-router";
import { goldRatesQueryOptions } from "@/lib/nonblocking-rates";
import { PakistanCityContent } from "@/components/pakistan/PakistanCityContent";
import { GoldCityAliasPage } from "@/components/GoldCityAliasPage";
import { canonicalCityPath, type GoldCityAlias } from "@/lib/alias-routes";

/**
 * Short-form city alias route: /pakistan/lahore, /india/mumbai.
 * Mirrors /city/$country/$city content but ranks for the bare URL pattern
 * that maps directly to "gold rate in lahore" SERPs.
 * Typos in either segment are normalized via the typo router — never 404.
 */
export const Route = createFileRoute("/$country/$city/")({
  beforeLoad: ({ params, location }) => {
    const c = params.country.toLowerCase();
    if (c !== "pakistan" && c !== "india") {
      const target = normalizeTypoPath(`${params.country}/${params.city}`);
      if (target && target !== location.pathname) {
        throw redirect({ to: target, replace: true });
      }
      throw notFound();
    }
  },
  parseParams: (params: Record<string, string>) => {
    if (params.country !== "pakistan" && params.country !== "india") throw notFound();
    return { country: params.country as Country, city: params.city };
  },
  loader: ({ context, params }) => {
    const country = params.country as Country;
    const c = findCity(country, params.city);
    if (!c) {
      const target = normalizeTypoPath(`${params.country}/${params.city}`);
      if (target && target !== `/${params.country}/${params.city}`) {
        throw redirect({ to: target, replace: true });
      }
      throw notFound();
    }
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
  },
  head: ({ params }) => {
    const country = params.country as Country;
    const city = findCity(country, params.city);
    const safeCity = { slug: params.city, name: city?.name ?? "City" };
    const head = cityHeadMeta(country, safeCity);
    // One canonical per city (see canonicalCityPath). Cities that own an
    // exact-match alias URL canonicalise there; the rest canonicalise here.
    const SITE_URL = "https://drhint.com";
    const canonicalPath = canonicalCityPath(country, params.city);
    const canonical = `${SITE_URL}${canonicalPath}`;
    const isCanonical = canonicalPath === `/${params.country}/${params.city}`;
    return {
      meta: [
        ...head.meta.filter((m: any) => m.property !== "og:url" && m.name !== "robots"),
        { property: "og:url", content: canonical },
        { name: "robots", content: isCanonical ? "index,follow,max-image-preview:large,max-snippet:-1" : "noindex,follow" },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: ShortCityPage,
  notFoundComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">City not found</h1>
        <p className="mt-2 text-muted-foreground">Try the country pages instead.</p>
        <div className="mt-4 flex gap-2 justify-center">
          <Link to="/pakistan" className="underline">Pakistan</Link>
          <Link to="/india" className="underline">India</Link>
        </div>
      </div>
    </Layout>
  ),
  errorComponent: ({ error }) => (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">Could not load rates</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
      </div>
    </Layout>
  ),
});

function ShortCityPage() {
  const { country, city: citySlug } = Route.useParams();
  const city = findCity(country, citySlug)!;
  const alias: GoldCityAlias = {
    slug: `${country}-${citySlug}`,
    country,
    citySlug,
    cityName: city.name,
  };
  const h1 = `Gold Rate in ${city.name} Today`;
  return (
    <>
      <GoldCityAliasPage alias={alias} h1={h1} />
      {country === "pakistan" && (
        <div className="mx-auto max-w-4xl px-4">
          <PakistanCityContent citySlug={citySlug} />
        </div>
      )}
    </>
  );
}